import { Localized, useLocalization } from "@fluent/react";
import {
    Alert,
    Box,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { AppId } from "../../../../app/appTypes";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { showError, showSuccess } from "../../../notifications/notificationsSlice";
import { SaveButton } from "../../../../app/components/SaveButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import CommentIcon from "@mui/icons-material/Comment";
import { isDocEditable, showDocComments } from "../../documentSlice";
import { SectionComments } from "../../SectionComments";
import { createDocumentDqMapping, DocumentDqMapping, DqMappingDocumentItem, DqMappingNote, updateDocumentDqMapping, updateDqMappingForReport, UpdateDqMappingReport } from "../documentDqMappingsApi";
import { unwrapResult } from "@reduxjs/toolkit";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import DescriptionIcon from "@mui/icons-material/Description";
import LockOutlined from "@mui/icons-material/LockOutlined";
import SourceIcon from "@mui/icons-material/Source";
import { RichTextContainer, RichTextEditor, tiptapExtensions } from "../../../../app/RichTextEditor";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import React from "react";
import { If } from "../../../../app/If";
import { CloseButton } from "../../../../app/components/CloseButton";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { useEditor } from "@tiptap/react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { DqMappingOriginChip, LockedIconWithTooltip } from "../components/DqMappingOriginUtils";
import { DocumentNumberBox } from "../../../../app/components/DocumentNumberBox";
import { GenerateDqMappingsFromAI } from "./GenerateDqMappingsFromAI";
import DeleteIcon from '@mui/icons-material/Delete';

export interface AddEditDocumentDqMappingProps {
    documentId: AppId;
    isProtocol: boolean;
    sectionId: string;
    mappingRow: DocumentDqMapping | null;
    mode: "create" | "edit" | "comment";
    onSuccessfulSave: (row: DocumentDqMapping) => void;
    onCancelClick: () => void;
}

function ReadonlyField({
    label,
    value,
}: {
    label: React.ReactNode;
    value: React.ReactNode;
    monospace?: boolean;
}) {
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
                {label}
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, mt: 0.5 }}>
                <Typography
                    variant="body1"
                    sx={{ whiteSpace: "pre-wrap" }}
                >
                    {value || " "}
                </Typography>
            </Paper>
        </Box>
    );
}

function ReadonlyHtmlField({
    label,
    html,
}: {
    label: React.ReactNode;
    html: string;
}) {
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
                {label}
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, mt: 0.5 }}>
                <Typography
                    variant="body1"
                    sx={{ "& p": { m: 0 }, whiteSpace: "normal" }}
                    dangerouslySetInnerHTML={{ __html: html || "" }}
                />
            </Paper>
        </Box>
    );
}


function SectionPanel({
    title,
    icon,
    chips,
    children,
}: {
    title: React.ReactNode;
    icon: React.ReactNode;
    chips?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 0,
                borderRadius: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Szary nagłówek */}
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: "grey.100",
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                {icon}
                <Typography variant="subtitle1" fontWeight={700}>
                    {title}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {chips}
            </Box>

            <Box sx={{ p: 2, flexGrow: 1 }}>{children}</Box>
        </Paper>
    );
}

export const AddEditDocumentDqMapping = ({
    documentId,
    isProtocol,
    sectionId,
    mappingRow,
    mode,
    onSuccessfulSave,
    onCancelClick,
}: AddEditDocumentDqMappingProps) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();

    const isEditable = useAppSelector(isDocEditable);
    const showComments = useAppSelector(state => showDocComments(state, sectionId))

    const [saving, setSaving] = useState(false);
    const [serverError, setServerError] = useState("");

    const [requirementNumber, setRequirementNumber] = useState<string>(mappingRow?.requirementNumber ?? "");
    const [requirementDescription, setRequirementDescription] = useState<string>(mappingRow?.requirementDescription ?? "");
    const [note, setNote] = useState<DqMappingNote | null>(mappingRow?.note ?? null);
    const [mappingComments, setMappingComments] = useState<string>(mappingRow?.mappingComment ?? "");

    const [touched, setTouched] = useState(false);
    const reqNumberError = touched && !requirementNumber.trim() && mode === "create";
    const reqDescError = touched && !requirementDescription.trim() && mode === "create";
    const [nextTempId, setNextTempId] = useState(-1);


    const [documentItems, setDocumentItems] = useState<DqMappingDocumentItem[]>(
        [...(mappingRow?.docMappings ?? [])].sort((a, b) => a.order - b.order)
    );
    const [selectedDocumentIdx, setSelectedDocumentIdx] = useState<number | null>(null);

    const [editMode, setEditMode] = useState<"none" | "edit" | "add">("none");

    const canEditReq = isEditable && isProtocol && !(mappingRow?.isRequirementFixed ?? false);
    const canEditMappings = isEditable && !isProtocol
    const [openAIDialog, setOpenAIDialog] = useState<boolean>(false);

    const editor = useEditor({
        extensions: tiptapExtensions,
        content: '<p>Initial content</p>',
        onUpdate: (e) => {
            if (selectedDocumentIdx != null) {
                patchFsItem(selectedDocumentIdx, { docDescription: e.editor.getHTML() });
            }

        },
    })

    const startAdd = () => {
        const draft: DqMappingDocumentItem = {
            id: nextTempId,
            mappingId: mappingRow?.id ?? 0,
            order: documentItems.length,
            docOrigin: "MANUAL",
            docNumber: "",
            docTitle: "",
            docDescription: "",
            mappingComment: "",
            isFsFixed: false,
        };

        setNextTempId(p => p - 1);
        setDocumentItems(items => [...items, draft]);
        setSelectedDocumentIdx(documentItems.length);
        editor?.commands.setContent(draft.docDescription);
        setEditMode("edit");
    };

    const cancelEdit = () => {
        setEditMode("none");
        setSelectedDocumentIdx(null);
    };

    const startEdit = (idx: number) => {
        setSelectedDocumentIdx(idx);
        setEditMode("edit");
        editor?.commands.setContent(documentItems[idx].docDescription)
    };

    const handleClearAllMappings = () => {
        if (!canEditMappings) return;
        setDocumentItems([]);
        setSelectedDocumentIdx(null);
        setEditMode("none");
    };


    const patchFsItem = (idx: number, patch: Partial<DqMappingDocumentItem>) => {
        setDocumentItems(items =>
            items.map((it, i) => (i === idx ? { ...it, ...patch } : it))
                .map((it, i) => ({ ...it, order: i }))
        );
    };

    const handleDocumentRowClick = (idx: number) => {

        if (selectedDocumentIdx === idx && editMode === "none") {
            setSelectedDocumentIdx(null);
            return;
        }
        setSelectedDocumentIdx(idx);
        setEditMode("none");
    };

    const handleRemoveDocument = (idx: number) => {
        if (!canEditMappings)
            return;

        if (selectedDocumentIdx === idx) {
            setEditMode("none");
            setSelectedDocumentIdx(null);
        }
        setDocumentItems((items) =>
            items
                .filter((_, i) => i !== idx)
                .map((x, i) => ({ ...x, order: i }))
        );
    };

    const handleGenerateAi = async () => {
        setOpenAIDialog(true);

    };

    const onSuccessfullyGeneratedMappings = (mappings: DocumentDqMapping) => {
        setOpenAIDialog(false);
        setDocumentItems(mappings.docMappings);

    };

    const handleDialogCancelClick = useCallback(() => {
        setOpenAIDialog(false);
    }, [])

    const NOTE_META: Record<DqMappingNote, {
        label: string;
        color: "success" | "error" | "default";
        Icon: typeof CheckCircleOutlineIcon;
    }> = {
        OK: { label: "Spełnia", color: "success", Icon: CheckCircleOutlineIcon },
        FAIL: { label: "Nie spełnia", color: "error", Icon: CancelOutlinedIcon },
        NA: { label: "Nie dotyczy", color: "default", Icon: RemoveCircleOutlineIcon },
    };
    const title = (
        <>
            {mode === "create" && <AddCircleIcon fontSize="small" sx={{ mr: 1 }} color="success" />}
            {mode === "edit" && <EditIcon fontSize="small" sx={{ mr: 1 }} color="info" />}
            {mode === "comment" && <CommentIcon fontSize="small" sx={{ mr: 1 }} color="info" />}
            <Localized id={mode === "create" ? "document-dq-mapping-add" : mode === "edit" ? "document-dq-mapping-edit" : "comments"}>
                <span>{mode === "create" ? "_Dodaj mapowanie" : mode === "edit" ? "_Edytuj mapowanie" : "_Komentarze"}</span>
            </Localized>
        </>
    );

    const save = async () => {

        if (isProtocol) {
            saveForProtocol();
        }
        else {
            saveForReport();
        }
    };

    const saveForProtocol = async () => {
        setTouched(true);
        if (mode === "create" && (!requirementNumber.trim() || !requirementDescription.trim())) {
            return;
        }

        setSaving(true);
        setServerError("");

        try {
            let saved: DocumentDqMapping;

            if (mode === "create") {
                const saved = await dispatch(
                    createDocumentDqMapping({
                        documentId,
                        sectionId,
                        payload: {
                            requirementNumber: requirementNumber.trim(),
                            requirementDescription: requirementDescription.trim(),
                            fsNumber: "",
                            fsTitle: "",
                            fsDescription: "",
                            mappingComment: mappingComments ?? "",
                            note: note ? note : null
                        },
                    })
                ).then(unwrapResult) as DocumentDqMapping;
                onSuccessfulSave(saved);
                dispatch(showSuccess("saved"));
            } else if (mode === "edit") {
                if (!mappingRow?.id) {
                    setServerError("Brak Id wpisu do edycji.");
                    setSaving(false);
                    return;
                }
                saved = await dispatch(updateDocumentDqMapping({
                    documentId,
                    sectionId,
                    id: mappingRow.id,
                    payload: {
                        requirementNumber: requirementNumber.trim(),
                        requirementDescription: requirementDescription.trim(),
                        fsNumber: "",
                        fsTitle: "",
                        fsDescription: "",
                        mappingComment: mappingComments ?? "",
                        note: note ? note : null

                    }
                })).unwrap();
                onSuccessfulSave(saved);
                dispatch(showSuccess("saved"));
            }

        } catch (e: any) {
            dispatch(showError("error"));
            const msg =
                e?.payload?.problem?.title ??
                e?.message ??
                "Wystąpił błąd podczas zapisu.";
            setServerError(msg);
        } finally {
            setSaving(false);
        }
    };

    const buildReportUpdatePayload = (): UpdateDqMappingReport => {

        return {
            note: note ?? null,
            mappingComment: mappingComments ?? "",
            documents: [...documentItems]
                .sort((a, b) => a.order - b.order)
                .map<DqMappingDocumentItem>((d, i) => ({
                    id: d.id,
                    mappingId: d.mappingId ?? (mappingRow?.id ?? 0),
                    order: i,
                    docOrigin: d.docOrigin,
                    docNumber: d.docNumber ?? "",
                    docTitle: d.docTitle ?? "",
                    docDescription: d.docDescription ?? "",
                    mappingComment: d.mappingComment ?? "",
                    isFsFixed: !!d.isFsFixed,
                })),
        };
    };

    const saveForReport = async () => {
        setTouched(true);
        setSaving(true);
        setServerError("");

        try {

            if (mode === "create") {
                throw new Error("Not supported now.");
            } else if (mode === "edit") {
                if (!mappingRow?.id) {
                    setServerError("Brak Id wpisu do edycji.");
                    setSaving(false);
                    return;
                }
                const updated = await dispatch(
                    updateDqMappingForReport({
                        documentId,
                        sectionId,
                        id: mappingRow.id,
                        payload: buildReportUpdatePayload(),
                    })

                ).then(unwrapResult) as DocumentDqMapping;

                onSuccessfulSave(updated);
                dispatch(showSuccess("saved"));
            }

        } catch (e: any) {
            dispatch(showError("error"));
            const msg =
                e?.payload?.problem?.title ??
                e?.message ??
                "Wystąpił błąd podczas zapisu.";
            setServerError(msg);
        } finally {
            setSaving(false);
        }
    };

    const form = (
        <Grid container spacing={2} alignItems="stretch">
            {/* URS */}
            <Grid item xs={12} md={6}>
                <SectionPanel
                    title={<Localized id="menu-urs">_URS – wymaganie</Localized>}
                    icon={<FactCheckIcon color="primary" fontSize="small" />}
                    chips={
                        <>
                            <DqMappingOriginChip origin={mappingRow?.requirementOrigin} />
                            {mappingRow?.isRequirementFixed && <LockedIconWithTooltip withLabel={true} />}

                        </>
                    }
                >
                    {canEditReq ? (
                        <TextField
                            label={l10n.getString("urs-requirement-no", null, "_Kod wymagania")}
                            value={requirementNumber}
                            onChange={(e) => setRequirementNumber(e.target.value)}
                            fullWidth
                            margin="normal"
                            required={mode === "create"}
                            error={reqNumberError}
                            helperText={reqNumberError ? l10n.getString("required", null, "_required") : " "}
                        />
                    ) : (
                        <ReadonlyField
                            label={l10n.getString("urs-requirement-no", null, "_Kod wymagania")}
                            value={requirementNumber}
                            monospace
                        />
                    )}

                    {canEditReq ? (
                        <>
                            <RichTextEditor value={requirementDescription} onValueChange={setRequirementDescription} label='urs-requirement-req' />
                        </>
                    ) : (
                        <ReadonlyHtmlField
                            label={l10n.getString("urs-requirement-req", null, "_Nazwa/treść wymagania")}
                            html={requirementDescription}
                        />
                    )}
                </SectionPanel>
            </Grid>

            {/* FS */}
            {isProtocol ? (
                <Grid item xs={12} md={6}>
                    <SectionPanel
                        title={<Localized id="document-dq-mapping-to-fs">_FS - dokumentacja</Localized>}
                        icon={<DescriptionIcon color="primary" fontSize="small" />}
                    >
                        <Alert severity="info" variant="outlined" sx={{ borderRadius: 2, alignItems: "flex-start" }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {l10n.getString("document-dq-mapping-report-only-title", null, "Mapowanie dokumentacji wykonasz na raporcie")}
                            </Typography>
                            <Typography variant="body2">
                                {l10n.getString("document-dq-mapping-report-only-description", null, "Dla protokolu ta sekcja jest informacyjna. Otworz raport, aby dodawac i edytowac mapowania FS.")}
                            </Typography>
                        </Alert>
                    </SectionPanel>
                </Grid>
            ) : (
                <Grid item xs={12} md={6} >
                    <SectionPanel
                        title={<Localized id="document-dq-mapping-to-fs">_FS – dokumentacja</Localized>}
                        icon={<DescriptionIcon color="primary" fontSize="small" />}

                    >
                        <>
                            <Paper
                                variant="outlined"
                                sx={{
                                    px: 2,
                                    py: 1.5,
                                    mb: 2,
                                    borderRadius: 2,
                                    position: "relative",
                                    bgcolor: canEditMappings ? "grey.50" : "grey.100",
                                    borderColor: canEditMappings ? "success.light" : "divider",
                                    "&:before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: 6,
                                        borderTopLeftRadius: 8,
                                        borderBottomLeftRadius: 8,
                                        bgcolor: "success.main",
                                    },
                                }}
                            >
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                            <Localized id="document-dq-mapping-note">_Ocena mapowania</Localized>
                                        </Typography>
                                    </Stack>

                                    <FormControl size="small" sx={{ width: { xs: "100%", sm: 240 } }}>
                                        <Select
                                            value={note}
                                            onChange={(e) => canEditMappings && setNote(e.target.value as DqMappingNote)}
                                            displayEmpty
                                            renderValue={(value) => {
                                                if (!value) {
                                                    return <Chip label="Brak oceny" variant="outlined" size="small" sx={{ width: "100%", justifyContent: "flex-start" }} />;
                                                }
                                                const m = NOTE_META[value as DqMappingNote];
                                                const { Icon } = m;
                                                return (
                                                    <Chip
                                                        icon={<Icon />}
                                                        label={m.label}
                                                        color={m.color === "default" ? undefined : m.color}
                                                        variant={m.color === "default" ? "outlined" : "filled"}
                                                        sx={{ ".MuiChip-icon": { mr: 0.5 }, width: "100%", justifyContent: "flex-start" }}
                                                    />
                                                );
                                            }}
                                            MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                                        >
                                            {(["OK", "FAIL", "NA"] as DqMappingNote[]).map((key) => {
                                                const m = NOTE_META[key]; const { Icon } = m;
                                                return (
                                                    <MenuItem
                                                        key={key}
                                                        value={key}
                                                        sx={{ py: 1, "& .MuiListItemIcon-root": { minWidth: 32 }, color: m.color === "default" ? "text.primary" : `${m.color}.main` }}
                                                    >
                                                        <ListItemIcon><Icon sx={{ color: m.color === "default" ? "text.secondary" : `${m.color}.main` }} /></ListItemIcon>
                                                        <ListItemText primary={m.label} />
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Stack>

                                <Box sx={{ mt: 1.5 }}>
                                    {canEditMappings ? (
                                        <TextField
                                            label={l10n.getString("document-dq-mapping-comment", null, "Komentarz do mapowania")}
                                            value={mappingComments}
                                            onChange={(e) => setMappingComments(e.target.value)}
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            sx={{ bgcolor: "background.paper" }}
                                        />
                                    ) : (
                                        <ReadonlyField
                                            label={l10n.getString("document-dq-mapping-comment", null, "Komentarz do mapowania")}
                                            value={mappingComments}
                                        />
                                    )}
                                </Box>
                            </Paper>


                            {/* LISTA DOKUMENTÓW + toolbar */}
                            <Paper
                                variant="outlined"
                                sx={{ p: 0, borderRadius: 2, mt: 2 }}
                            >
                                {/* NAGŁÓWEK */}
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        bgcolor: "grey.100",
                                        borderBottom: 1,
                                        borderColor: "divider",
                                        borderTopLeftRadius: 8,
                                        borderTopRightRadius: 8,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ textTransform: "uppercase" }}>
                                        <Localized id="document-dq-mapping-doc-list">_Lista odniesień w dokumentach</Localized>
                                    </Typography>
                                    <Box sx={{ flex: 1 }} />
                                    {canEditMappings && (
                                        <Stack direction="row" spacing={1}>
                                            <Chip
                                                icon={<AddCircleIcon color="success" />}
                                                label="Dodaj"
                                                onClick={startAdd}
                                                color="success"
                                                variant="outlined"
                                            />
                                            <Chip
                                                icon={<DeleteSweepIcon />}
                                                label="Wyczyść"
                                                onClick={handleClearAllMappings}
                                                variant="outlined"
                                                color="error"
                                            />
                                            <Chip
                                                icon={<AutoAwesomeIcon color="info" />}
                                                label="Generuj AI"
                                                onClick={handleGenerateAi}
                                                variant="outlined"
                                                color="info"
                                            />
                                        </Stack>
                                    )}
                                </Box>

                                {/* TREŚĆ */}
                                <Box sx={{ p: 2 }}>
                                    <Stack spacing={1}>
                                        {(documentItems.length > 0 ? documentItems : []).map((item, idx) => (
                                            <Paper
                                                key={item.id ?? idx}
                                                variant="outlined"
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                }}
                                                onClick={() => handleDocumentRowClick(idx)}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Chip size="small" label={`#${idx + 1}`} />
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="subtitle2" sx={{ minWidth: 0 }}>
                                                            {item.docTitle || "(bez tytułu)"}
                                                        </Typography>
                                                        <DocumentNumberBox value={item.docNumber} />

                                                    </Box>
                                                    <Box sx={{ flex: 1 }} />
                                                    <Stack direction="row" spacing={1}>
                                                        <DqMappingOriginChip origin={item?.docOrigin} />
                                                        {item?.isFsFixed && <LockedIconWithTooltip withLabel={true} />}
                                                        {canEditMappings && <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />}

                                                        {canEditMappings && (
                                                            <Stack
                                                                direction="row"
                                                                spacing={0.5}
                                                                alignItems="center"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <Tooltip title="Edytuj">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => { e.stopPropagation(); startEdit(idx); }}
                                                                        sx={(t) => ({
                                                                            bgcolor: "background.paper",
                                                                            "&:hover": {
                                                                                color: t.palette.info.main,
                                                                                bgcolor: t.palette.action.hover,
                                                                            },
                                                                        })}
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>

                                                                <Tooltip title="Usuń">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => { e.stopPropagation(); handleRemoveDocument(idx); }}
                                                                        sx={(t) => ({
                                                                            bgcolor: "background.paper",
                                                                            "&:hover": {
                                                                                color: t.palette.error.main,
                                                                                bgcolor: t.palette.action.hover,
                                                                            },
                                                                        })}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Stack>
                                                        )}

                                                    </Stack>
                                                </Stack>

                                                {editMode === "none" && selectedDocumentIdx === idx && (
                                                    <Box sx={{ mt: 1 }}>
                                                        <ReadonlyHtmlField label={l10n.getString("description", null, "_Opis (FS)")} html={item.docDescription ?? ""} />
                                                        <ReadonlyField label={l10n.getString("document-dq-mapping-comment", null, "_Komentarz")} value={item.mappingComment} />
                                                    </Box>
                                                )}
                                            </Paper>
                                        ))}

                                        {documentItems.length === 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                <Localized id="no-documents"><span>_No documents</span></Localized>
                                            </Typography>
                                        )}
                                    </Stack>

                                    {/* Formularz edycji / dodawania pod listą */}
                                    {canEditMappings && editMode === "edit" && selectedDocumentIdx != null && (
                                        <Box sx={{ p: 2, borderRadius: 2, mt: 2 }}>
                                            <Box
                                                sx={{
                                                    px: 2,
                                                    py: 1,
                                                    mb: 2,
                                                    bgcolor: "grey.100",
                                                    border: 1,
                                                    borderColor: "divider",
                                                    borderRadius: 2,
                                                }}
                                            >
                                                {(() => {
                                                    const it = documentItems[selectedDocumentIdx];
                                                    return (
                                                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                            <Box>
                                                                <Chip size="small" color="primary" label={`Edytujesz: #${selectedDocumentIdx + 1}`} />
                                                                <Typography variant="subtitle2">{it.docTitle || "(bez tytułu)"}</Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                                    {it.docNumber || "(brak numeru)"}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ flex: 1 }} />
                                                            <Chip
                                                                size="small"
                                                                icon={<SourceIcon fontSize="small" />}
                                                                label={` ${it.docOrigin === "SYSTEM" ? "System" : "Manual"}`}
                                                                color={it.docOrigin === "SYSTEM" ? "primary" : "default"}
                                                                variant={it.docOrigin === "SYSTEM" ? "filled" : "outlined"}
                                                            />
                                                            {it.isFsFixed && (
                                                                <Chip size="small" icon={<LockOutlined fontSize="small" />} label="Locked" variant="outlined" />
                                                            )}
                                                        </Stack>
                                                    );
                                                })()}
                                            </Box>

                                            {/* Pola edycji */}
                                            <TextField
                                                label={l10n.getString("document-number-long", null, "_Numer")}
                                                value={documentItems[selectedDocumentIdx].docNumber}
                                                onChange={(e) => patchFsItem(selectedDocumentIdx, { docNumber: e.target.value })}
                                                fullWidth margin="dense" size="small" placeholder="np. FS-12.3"
                                            />
                                            <TextField
                                                label={l10n.getString("document-title", null, "_Tytuł")}
                                                value={documentItems[selectedDocumentIdx].docTitle}
                                                onChange={(e) => patchFsItem(selectedDocumentIdx, { docTitle: e.target.value })}
                                                fullWidth margin="dense" size="small" placeholder="Tytuł dokumentu"
                                            />

                                            <RichTextContainer label="Example" editor={editor} />

                                            <TextField
                                                label={l10n.getString("comment", null, "_Komentarz")}
                                                value={documentItems[selectedDocumentIdx].mappingComment}
                                                onChange={(e) => patchFsItem(selectedDocumentIdx, { mappingComment: e.target.value })}
                                                fullWidth margin="dense" multiline minRows={2} placeholder="Uwagi, kontekst, decyzje"
                                            />

                                            <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: "flex-end" }}>
                                                <Chip label="Zamknij edycję" variant="outlined" onClick={cancelEdit} />
                                            </Stack>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>

                        </>
                    </SectionPanel>
                </Grid>
            )}

            {serverError && (
                <Grid item xs={12}>
                    <Alert severity="error">{serverError}</Alert>
                </Grid>
            )}
        </Grid>
    );


    const content = mappingRow?.guid
        ? (
            <If condition={showComments} otherwise={<Box>{form}</Box>}>
                <SectionComments
                    sectionId={sectionId}
                    contextType="dq_mapping"
                    contextGuid={mappingRow.guid}
                    component={<Box>{form}</Box>}
                />
            </If>
        )
        : form;

    return (
        <Dialog open fullScreen fullWidth keepMounted={false}>
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: "#f9f9f9",
                    color: "text.primary",
                    fontWeight: "bold",
                }}
            >
                {title}
            </DialogTitle>

            <DialogContent dividers>{content}</DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: "flex-end" }}>
                <SaveButton disabled={!isEditable} loading={saving} onClick={save} />
                <CloseButton loading={saving} onClick={onCancelClick} />
            </DialogActions>
            {openAIDialog && (
                <GenerateDqMappingsFromAI
                    documentId={documentId}
                    sectionId={sectionId}
                    mappingId={mappingRow?.id ?? 0}
                    onSuccessfulSave={onSuccessfullyGeneratedMappings}
                    onCancel={handleDialogCancelClick}
                />
            )}
        </Dialog>
    );
};
